<template>
  <v-app id="admin-app" class="admin-layout">
    <!-- 상단 네비게이션 바 -->
    <v-app-bar color="#1a237e" elevation="2" height="56" class="admin-app-bar">
      <v-app-bar-nav-icon @click="drawer = !drawer" color="white"></v-app-bar-nav-icon>
      
      <v-toolbar-title class="admin-title">
        <v-icon class="mr-2" color="amber">mdi-shield-crown</v-icon>
        CAION Admin
      </v-toolbar-title>
      
      <v-spacer></v-spacer>
      
      <v-chip color="amber" variant="flat" size="small" class="mr-4">
        <v-icon start size="small">mdi-shield-account</v-icon>
        관리자
      </v-chip>
      
      <!-- 사용자 정보 및 로그아웃 -->
      <v-menu class="user-menu">
        <template v-slot:activator="{ props }">
          <v-btn icon v-bind="props" class="mr-2" color="white">
            <v-icon>mdi-account-circle</v-icon>
          </v-btn>
        </template>
        <v-list>
          <v-list-item @click="handleLogout">
            <v-list-item-title>
              <v-icon size="small" class="mr-1">mdi-logout</v-icon>
              로그아웃
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-app-bar>

    <!-- 사이드바 네비게이션 -->
    <v-navigation-drawer v-model="drawer" temporary class="admin-drawer">
      <v-list-item class="admin-drawer-header">
        <template v-slot:prepend>
          <v-avatar color="amber" size="40">
            <v-icon color="white">mdi-shield-crown</v-icon>
          </v-avatar>
        </template>
        <v-list-item-title class="admin-drawer-title">관리자 메뉴</v-list-item-title>
        <v-list-item-subtitle>Administrator Panel</v-list-item-subtitle>
      </v-list-item>
      
      <v-divider></v-divider>
      
      <v-list nav>
        <template v-for="item in menuItems" :key="item.title">
          <!-- 일반 메뉴 아이템 -->
          <v-list-item
            v-if="!item.children"
            :to="item.to"
            :prepend-icon="item.icon"
            :title="item.title"
            class="admin-menu-item"
            active-class="admin-menu-active"
          ></v-list-item>
          
          <!-- 리스트 그룹 -->
          <v-list-group
            v-else
            :value="item.title"
          >
            <template v-slot:activator="{ props }">
              <v-list-item
                v-bind="props"
                :prepend-icon="item.icon"
                :title="item.title"
                class="admin-menu-item"
              ></v-list-item>
            </template>
            
            <v-list-item
              v-for="child in item.children"
              :key="child.title"
              :to="(child as any).to || undefined"
              :prepend-icon="child.icon"
              :title="child.title"
              class="admin-menu-item admin-submenu-item"
              active-class="admin-menu-active"
              @click="(child as any).action ? handleMenuAction((child as any).action) : null"
            ></v-list-item>
          </v-list-group>
        </template>
      </v-list>
    </v-navigation-drawer>

    <!-- 메인 콘텐츠 -->
    <v-main class="admin-main">
      <v-container fluid class="pa-4">
        <slot />
      </v-container>
    </v-main>
    
    <!-- 공휴일 관리 컴포넌트 -->
    <HolidayRemote ref="holidayRemoteRef" />
    
    <!-- 수가 관리 컴포넌트 -->
    <SugaRemote ref="sugaRemoteRef" />
    
    <!-- 세금 요율 관리 컴포넌트 -->
    <TaxRateRemote ref="taxRateRemoteRef" />
  </v-app>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import HolidayRemote from "@/components/common/HolidayRemote.vue";
import SugaRemote from "@/components/common/SugaRemote.vue";
import TaxRateRemote from "@/components/common/TaxRateRemote.vue";

const store = useStore();
const router = useRouter();

// HolidayRemote 컴포넌트 ref
const holidayRemoteRef = ref<{ openDialog: () => void } | null>(null);

// SugaRemote 컴포넌트 ref
const sugaRemoteRef = ref<{ openDialog: () => void } | null>(null);

// TaxRateRemote 컴포넌트 ref
const taxRateRemoteRef = ref<{ openDialog: () => void } | null>(null);

// 사이드바 상태
const drawer = ref(false);

// 사용자 정보 (Vuex에서 가져오기)
const userInfo = computed(() => store.getters.currentUser);

// Admin 메뉴 아이템들
const menuItems = ref([
  {
    title: '회원 관리',
    icon: 'mdi-account-group',
    to: '/admin/members'
  },
  {
    title: '공지사항',
    icon: 'mdi-bullhorn',
    to: '/admin/notice'
  },
  {
    title: '기타 관리',
    icon: 'mdi-cog',
    children: [
      {
        title: '공휴일 관리',
        icon: 'mdi-calendar',
        action: 'openHolidayRemote'
      },
      {
        title: '수가 관리',
        icon: 'mdi-cash-multiple',
        action: 'openSugaRemote'
      },
      {
        title: '세금 요율 관리',
        icon: 'mdi-percent',
        action: 'openTaxRateRemote'
      },
      {
        title: '가격 정책',
        icon: 'mdi-tag-heart',
        to: '/admin/pricing'
      },
      {
        title: '설 특집 쇼핑몰',
        icon: 'mdi-gift',
        to: '/admin/shop'
      }
    ]
  }
]);

// 공휴일 관리 다이얼로그 열기
const openHolidayRemote = () => {
  if (holidayRemoteRef.value) {
    holidayRemoteRef.value.openDialog();
  }
};

// 수가 관리 다이얼로그 열기
const openSugaRemote = () => {
  if (sugaRemoteRef.value) {
    sugaRemoteRef.value.openDialog();
  }
};

// 세금 요율 관리 다이얼로그 열기
const openTaxRateRemote = () => {
  if (taxRateRemoteRef.value) {
    taxRateRemoteRef.value.openDialog();
  }
};

// 메뉴 액션 처리
const handleMenuAction = (action: string) => {
  if (action === 'openHolidayRemote') {
    openHolidayRemote();
  } else if (action === 'openSugaRemote') {
    openSugaRemote();
  } else if (action === 'openTaxRateRemote') {
    openTaxRateRemote();
  }
};

// 로그아웃 처리
const handleLogout = async () => {
  try {
    await store.dispatch('performLogout');
    router.push('/pgm/login');
  } catch (error) {
    console.error('로그아웃 오류:', error);
  }
};

onMounted(async () => {
  await store.dispatch('validateAuth');
  
  // Admin이 아닌 경우 일반 페이지로 리다이렉트
  if (!store.getters.isAdmin) {
    router.push('/pgm/wage/WageProcess');
    return;
  }
});
</script>

<style scoped>
@import '../../assets/styles/adminLayout.scss';
</style>

